import { getLogger } from 'rcd-cpp-logger';
import ThruCd from 'rcd-cpp-workspace/db/thruCd';
import { getUser } from './getUser';
import { getContentTypeConfig } from './getContentTypeConfig';
import {
  createLightUpdate,
  updateLightUpdateConfigureWorkflow,
  updateConfigureWorkflow,
  updateEFSPersonas
} from './upsertItemGateway';
import {
  getDocumentSubtypes,
  getSubtypeContextList
} from './getResearchModels';
import { Functions } from '../types/types';
import {
  searchGroups,
  searchData,
  getDataDetail,
  searchEmployee,
  searchLucene,
  getBoardCardCount,
  getLastCreatedItem
} from './search';
import { exportData } from './exportData';
import {
  getTree,
  getAllTree,
  getWorkspace,
  getWorkspaces,
  getUserExport,
  getUserSettings,
  searchReferences,
  getComments,
  getHomeWorkspace,
  getDataMapping,
  getPracticeCors,
  getViewInfo,
  getExpiryHome,
  getUserBackups,
  getEFSDataDetail,
  getWorkflowSteps,
  getWorkflowInfo,
  getContentTypeConfigurations,
  getAssociatedDelegates,
  exportRolePriorityTree
} from './metadata';
import {
  searchPermission,
  getPermissions,
  upsertPermission,
  deletePermission
} from './permission';
import {
  createData,
  updateData,
  deleteData,
  createComment,
  updateComment,
  deleteComment,
  pushToCPP,
  runRules
} from './mutation';
import {
  cloneView,
  createView,
  updateView,
  deleteView,
  moveView,
  createSchema,
  updateSchema,
  deleteSchema,
  updateFolder,
  createFolder,
  deleteFolder,
  createWorkspace,
  upsertWorkspaceSettings,
  upsertUserSettings,
  getUserInfo,
  cloneFolder
} from './admin';
import { generateSchemaJson } from './local';
import { getDocInfo } from './docInfo';
import { AlertType } from '../types/AlertType';
import {
  createUniOfRelationship,
  addProjectDerivativesRelationship,
  removeProjectDerivativesRelationship
} from './docMutation';
import {
  addBackTags,
  searchStagedTags,
  getStagedTagsItemCount,
  validateBacktagImportData,
  publishStagedTags,
  importBacktagData,
  getBacktagImport
} from './backtag';
import { importManagedData } from './backtag';

export type FetchWithToken = (
  endpoint?: string,
  req?: Request,
  opts?: ReqOpts
) => Promise<Response>;

interface Request extends Omit<RequestInit, 'headers'> {
  headers?: HeadersInit & { Authorization?: string; Accept?: string };
  type?: string;
}

const getFetchWithToken =
  (fns: Partial<Functions>): FetchWithToken =>
  async (endpoint: string, req: Request = {}, opts: ReqOpts = {}) => {
    const logger = getLogger('CPPPlan');
    if (!fns?.getToken) {
      throw new Error('GetToken function is not set.');
    }
    let url = endpoint;
    if (!url.startsWith('http')) {
      url = process.env.CPP_PLAN_URI + endpoint;
    }
    if (req.cache == 'force-cache') {
      url = wrapUrlWithBustParams(url, opts);
    }
    if (req.headers == null) {
      req.headers = {};
    }
    if (req.headers.Accept == null) {
      req.headers.Accept = 'application/json';
    }
    if (!req.method) {
      req.method = 'GET';
    }
    const setAuthorizationHeader = async (newToken?: boolean) => {
      let token = await fns.getToken(newToken);
      if (!token?.accessToken) {
        throw new Error('Failed to get token.');
      }
      if (!token?.thruCd) {
        throw new Error('Invalid Thru Cd.');
      }
      let { thruCd, accessToken } = token;
      if (thruCd == ThruCd.CPPD && !accessToken.startsWith('Bearer')) {
        accessToken = 'Bearer ' + accessToken;
      }

      req.headers.Authorization = accessToken;
      req.headers['cpp-thru-cd'] = thruCd;
      //Auth done via cpp content OAuthorizer. Does not support the below header.
      if (
        url &&
        process.env.CPP_PLAN_URI &&
        (!url.startsWith('http') || url.startsWith(process.env.CPP_PLAN_URI))
      ) {
        req.headers['cpp-version'] = process.env.VERSION;
      }
    };

    if (!req.headers.Authorization) {
      await setAuthorizationHeader();
    }

    const pathname = new URL(url).pathname;

    const doFetch = async (attempts: number = 0) => {
      if (attempts) {
        logger.debug(
          'Making #%s attempts to %s %s',
          attempts,
          req.method,
          pathname
        );
      }
      logger.startTimer();
      if (!url.includes('execute-api') && !url.endsWith('/document')) {
        req.headers['cpp-log-reqId'] = Math.random().toString(36).substring(2);
      }
      const matches = window?.location?.pathname?.match(/^\/w\/([^\/?&#]+)/);
      if (url.startsWith(process.env.CPP_PLAN_URI)) {
        if (matches?.length) {
          req.headers['cpp-workspace-id'] = matches[1];
        } else if ((fns as any).workspaceId) {
          req.headers['cpp-workspace-id'] = (fns as any).workspaceId;
        }
      }
      let response = null;
      try {
        response = await fetch(url, req);
      } catch (e) {
        console.error(e);
        if (fns.setShowVpnError) {
          fns.setShowVpnError(true);
        }
        throw e;
      }
      const ms = logger.endTimer();
      if (ms > 200) {
        logger.info(
          '[PERF] %s %s with status %d took %dms [%s]',
          req.method,
          pathname,
          response.status,
          ms,
          req.headers['cpp-log-reqId']
        );
      }
      if (response.status == 401 || response.status == 403) {
        if (attempts > 0) {
          throw new Error(
            `Failed to call api with status (${response.status})`
          );
        }
        await setAuthorizationHeader();
        return await doFetch(attempts + 1);
      }
      if (response.status >= 200 && response.status < 300) {
        return response;
      } else {
        let errMsg = `${req.method} to ${pathname} failed.`;
        let additionalInfo = undefined;
        let name = undefined;
        let type = undefined;
        try {
          const resp = await response.json();
          if (resp.message) {
            errMsg = resp.message;
          }
          additionalInfo = resp.additionalInfo;
          if (!additionalInfo) additionalInfo = {};
          additionalInfo.reqLogId = req.headers['cpp-log-reqId'];
          additionalInfo.endpoint = endpoint;
          name = resp.name;
          type = resp.type;
        } catch (err) {}
        throw new APIError(errMsg, additionalInfo, type, name);
      }
    };

    return await doFetch();
  };

export class APIError<T> extends Error {
  type = 'error';
  additionalInfo: T;
  constructor(msg: string, additionalInfo: T, type?: AlertType, name?: string) {
    super(msg);
    this.additionalInfo = additionalInfo;
    if (type) this.type = type;
    this.name = name || 'APIError';
  }
}

interface GraphQLReq {
  query: string;
  variables?: { [x: string]: any };
}

const cleanGQLQuery = (query: string) =>
  query
    ?.split('\n')
    .join(' ')
    .split(' ')
    .filter(x => x)
    .join(' ');

const wrapUrlWithBustParams = (url: string, opts: ReqOpts): string => {
  url += url.indexOf('?') > 0 ? '&' : '?';
  const hour = opts.maxCacheHour || 8;
  // bust cache after 8 hours
  url += 'cache-bust=' + ((Date.now() / 1000 / 60 / 60 / hour) | 0);
  // bust cache on new version change
  url += '&version=' + process.env.VERSION;
  // bust cache with System Setting cacheBuster
  if (cacheBuster) url += `&ss-buster=${cacheBuster}`;
  return url;
};

type ReqOpts = { maxCacheHour?: number };
type Req = Partial<RequestInit> & ReqOpts;

export class Fetch {
  public fetchWithToken: FetchWithToken;
  constructor(fns: Partial<Functions>) {
    this.fetchWithToken = getFetchWithToken(fns);
  }
  public async getJSON<R>(endpoint: string, req?: Req): Promise<R> {
    return await this.fetchJSON(undefined, endpoint, 'GET', req);
  }
  public async fetchJSON<R, P = any>(
    payload: P,
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'POST',
    { maxCacheHour, ...req }: Req = {}
  ): Promise<R> {
    const resp = await this.fetchWithToken(
      endpoint,
      {
        ...req,
        method,
        body: JSON.stringify(payload)
      },
      { maxCacheHour }
    );
    return await resp.json();
  }
  public async graphQLFetch<T = any>(
    endpoint: string,
    graphqlBody: string | GraphQLReq,
    req?: Partial<Request>
  ): Promise<T> {
    let body = graphqlBody as string;
    if (typeof graphqlBody == 'string') {
      body = cleanGQLQuery(graphqlBody);
    } else if (graphqlBody.query) {
      body = JSON.stringify({
        ...graphqlBody,
        query: cleanGQLQuery(graphqlBody.query)
      });
    }

    const resp = await this.fetchWithToken(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body,
      ...req
    });
    return await resp.json();
  }
}

let cacheBuster = null;

export const createApi = (fns: Partial<Functions>) => {
  const _fetch = new Fetch(fns);
  return {
    setCacheBuster: (bust: any) => (cacheBuster = bust),
    fetchJSON: async <P>(payload: P, url: string): Promise<any> =>
      _fetch.fetchJSON<any>(payload, url, 'POST'),
    getDocInfo: getDocInfo(_fetch),
    createUniOfRelationship: createUniOfRelationship(_fetch),
    addProjectDerivativesRelationship:
      addProjectDerivativesRelationship(_fetch),
    removeProjectDerivativesRelationship:
      removeProjectDerivativesRelationship(_fetch),
    exportData: exportData(_fetch),
    getUserExport: getUserExport(_fetch),
    getTree: getTree(_fetch),
    getContentTypeConfigurations: getContentTypeConfigurations(_fetch),
    getDataMapping: getDataMapping(_fetch),
    getPracticeCors: getPracticeCors(_fetch),
    getAllTree: getAllTree(_fetch),
    getWorkspace: getWorkspace(_fetch),
    getWorkspaces: getWorkspaces(_fetch),
    searchReferences: searchReferences(_fetch),
    getPermissions: getPermissions(_fetch),
    searchPermission: searchPermission(_fetch),
    upsertPermission: upsertPermission(_fetch),
    deletePermission: deletePermission(_fetch),
    importBacktagData: importBacktagData(_fetch),
    validateBacktagImportData: validateBacktagImportData(_fetch),
    getBacktagImport: getBacktagImport(_fetch),
    searchStagedTags: searchStagedTags(_fetch),
    publishStagedTags: publishStagedTags(_fetch),
    getStagedTagsItemCount: getStagedTagsItemCount(_fetch),
    createLightUpdate: createLightUpdate(_fetch),
    updateConfigureWorkflow: updateConfigureWorkflow(_fetch),
    updateLightUpdateConfigureWorkflow:
      updateLightUpdateConfigureWorkflow(_fetch),
    updateEFSPersonas: updateEFSPersonas(_fetch),
    getWorkflowSteps: getWorkflowSteps(_fetch),
    getWorkflowInfo: getWorkflowInfo(_fetch),
    getContentTypeConfiguration: getContentTypeConfig(_fetch),
    getDocumentSubtypes: getDocumentSubtypes(_fetch),
    getSubtypeContextList: getSubtypeContextList(_fetch),
    cloneView: cloneView(_fetch),
    cloneFolder: cloneFolder(_fetch),
    createData: createData(_fetch),
    updateData: updateData(_fetch),
    pushToCPP: pushToCPP(_fetch),
    runRules: runRules(_fetch),
    addBackTags: addBackTags(_fetch),
    deleteData: deleteData(_fetch),
    createView: createView(_fetch),
    updateView: updateView(_fetch),
    deleteView: deleteView(_fetch),
    moveView: moveView(_fetch),
    getHomeWorkspace: getHomeWorkspace(_fetch),
    getExpiryHome: getExpiryHome(_fetch),
    createFolder: createFolder(_fetch),
    updateFolder: updateFolder(_fetch),
    deleteFolder: deleteFolder(_fetch),
    createSchema: createSchema(_fetch),
    updateSchema: updateSchema(_fetch),
    deleteSchema: deleteSchema(_fetch),
    getBoardCardCount: getBoardCardCount(_fetch),
    searchGroups: searchGroups(_fetch),
    searchData: searchData(_fetch),
    searchLucene: searchLucene(_fetch),
    searchEmployee: searchEmployee(_fetch),
    getDataDetail: getDataDetail(_fetch),
    getLastCreatedItem: getLastCreatedItem(_fetch),
    getEFSDataDetail: getEFSDataDetail(_fetch),
    getViewInfo: getViewInfo(_fetch),
    getUser: getUser(_fetch),
    getUserInfo: getUserInfo(_fetch),
    getUserBackups: getUserBackups(_fetch),
    getUserSettings: getUserSettings(_fetch),
    getComments: getComments(_fetch),
    upsertUserSettings: upsertUserSettings(_fetch),
    upsertWorkspaceSettings: upsertWorkspaceSettings(_fetch),
    createWorkspace: createWorkspace(_fetch),
    createComment: createComment(_fetch),
    updateComment: updateComment(_fetch),
    deleteComment: deleteComment(_fetch),
    generateSchemaJson: generateSchemaJson(_fetch),
    getAssociatedDelegates: getAssociatedDelegates(_fetch),
    exportRolePriorityTree: exportRolePriorityTree(_fetch),
    importManagedData: importManagedData(_fetch)
  };
};

export type API = ReturnType<typeof createApi>;
